import { create } from 'zustand';
import { api } from '../api/client';
import type { User, AuthResponse } from '../types';
import { getVKUserProfile, isVKEnvironment } from '../api/vk';
import { clearOAuthFlow } from '../api/vkOAuth';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /** Only true during initial loadUser(). Never for login buttons. */
  isLoading: boolean;
  error: string | null;
  login: (opts: { init_data?: string; name?: string }) => Promise<void>;
  /** Apply an already-obtained AuthResponse (OAuth / VK ID) to the store. */
  setSession: (res: AuthResponse) => void;
  autoLoginVK: () => Promise<boolean>;
  logout: () => void;
  loadUser: () => Promise<void>;
}

let bootstrapped = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem('colibri_token'),
  isAuthenticated: !!localStorage.getItem('colibri_token'),
  isLoading: true,
  error: null,

  login: async ({ init_data, name }) => {
    // Do NOT flip global isLoading — that unmounts the router
    set({ error: null });
    const payload = { init_data: init_data || name || 'guest' };
    const res = await api.post<AuthResponse>('auth/vk-login', payload);
    get().setSession(res);
  },

  setSession: (res) => {
    localStorage.setItem('colibri_token', res.token);
    set({ user: res.user, token: res.token, isAuthenticated: true, isLoading: false, error: null });
  },

  autoLoginVK: async () => {
    if (bootstrapped) return get().isAuthenticated;
    bootstrapped = true;

    if (!isVKEnvironment()) {
      set({ isLoading: false });
      return false;
    }
    if (localStorage.getItem('colibri_token')) {
      // Token already handled by loadUser
      return get().isAuthenticated;
    }
    try {
      const profile = await getVKUserProfile();
      if (!profile) {
        set({ isLoading: false });
        return false;
      }
      const res = await api.post<AuthResponse>('auth/vk-login', {
        init_data: profile.raw_query || JSON.stringify(profile),
      });
      localStorage.setItem('colibri_token', res.token);
      set({ user: res.user, token: res.token, isAuthenticated: true, isLoading: false });
      return true;
    } catch (e) {
      console.error('VK auto-login failed:', e);
      set({ isLoading: false, error: 'Не удалось войти через VK', isAuthenticated: false });
      return false;
    }
  },

  logout: () => {
    const token = localStorage.getItem('colibri_token');
    if (token) {
      // best-effort server-side session revocation, ignore errors
      void api.post('auth/logout').catch(() => {});
    }
    localStorage.removeItem('colibri_token');
    clearOAuthFlow();
    bootstrapped = false;
    set({ user: null, token: null, isAuthenticated: false, isLoading: false, error: null });
  },

  loadUser: async () => {
    // Safety: never leave the app on a spinner forever
    const failSafe = setTimeout(() => {
      set({ isLoading: false });
    }, 12000);

    try {
      const token = localStorage.getItem('colibri_token');
      if (!token) {
        if (isVKEnvironment()) {
          try {
            const ok = await get().autoLoginVK();
            if (ok) return;
          } catch {
            /* fall through */
          }
        }
        set({ isLoading: false, isAuthenticated: false });
        return;
      }
      try {
        const user = await api.get<User>('auth/me');
        set({ user, isAuthenticated: true, isLoading: false });
      } catch {
        localStorage.removeItem('colibri_token');
        if (isVKEnvironment()) {
          try {
            bootstrapped = false;
            const ok = await get().autoLoginVK();
            if (ok) return;
          } catch {
            /* fall through */
          }
        }
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    } finally {
      clearTimeout(failSafe);
      // Always clear if still loading
      if (get().isLoading) set({ isLoading: false });
    }
  },
}));
